import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { profile } from '../data/mock';
import { Mail, Linkedin, MapPin, Download, Send, ArrowRight, QrCode, Phone, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Contact = () => {
  const { t, language } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [siteUrl, setSiteUrl] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') setSiteUrl(window.location.origin);
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      // Cloudflare Pages Function returns JSON; local CRA dev server
      // returns index.html (200, HTML) because the function is not running.
      const contentType = res.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await res.json() : null;

      if (res.ok && data && data.ok) {
        toast.success(t('contact.success'));
        setForm({ name: '', email: '', subject: '', message: '' });
      } else if (!contentType.includes('application/json')) {
        // Dev fallback: no Worker running. Persist locally so the UX is still usable.
        const stored = JSON.parse(localStorage.getItem('contact_messages') || '[]');
        stored.push({ ...form, at: new Date().toISOString() });
        localStorage.setItem('contact_messages', JSON.stringify(stored));
        toast.success(t('contact.success'));
        setForm({ name: '', email: '', subject: '', message: '' });
      } else {
        toast.error(t('contact.error'));
      }
    } catch {
      toast.error(t('contact.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCVDownload = (e) => {
    // Let the browser handle the native download; no extra logic needed.
    toast.success(
      language === 'de' ? 'Lebenslauf wird geöffnet…' : 'Opening CV…'
    );
  };

  return (
    <div>
      {/* Header */}
      <section className="relative border-b border-white/10 sky-section overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[2px] cfd-bar" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
          <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#D95871] mb-3">
            <MessageSquare className="w-4 h-4" />
            <span className="w-8 h-px bg-[#D95871]" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-50 tracking-tight">
            {t('contact.title')}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-14 lg:py-20">
        {/* Availability headline */}
        <div className="mb-10 p-6 sm:p-8 bg-[#1A2231] border-l-4 border-[#4A8FD1] border-t border-r border-b border-white/10 rounded-r-lg rounded-bl-lg">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-50 mb-2">
            {t('cta.headline')}
          </h2>
          <p className="text-slate-300 leading-relaxed">
            {t('cta.line')}
          </p>
          <p className="mt-2 text-sm text-slate-500 italic leading-relaxed">
            {t('cta.note')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Direct contact */}
          <aside className="lg:col-span-5 space-y-4">
            <a
              href={`mailto:${profile.email}`}
              className="flex items-start gap-4 p-5 bg-[#1A2231] border border-white/10 rounded-lg hover:border-[#4A8FD1] hover:shadow-sm transition-all group"
            >
              <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#4A8FD1]/10">
                <Mail className="w-5 h-5 text-[#4A8FD1]" />
              </div>
              <div className="flex-1">
                <div className="text-xs mono uppercase tracking-widest text-slate-500 mb-1">
                  {t('contact.directEmail')}
                </div>
                <div className="font-medium text-slate-50 break-all">
                  {profile.email}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#4A8FD1] mt-2" />
            </a>

            <a
              href={`tel:${profile.phoneClean}`}
              className="flex items-start gap-4 p-5 bg-[#1A2231] border border-white/10 rounded-lg hover:border-[#4A8FD1]/50 hover:shadow-sm transition-all group"
            >
              <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#4A8FD1]/15">
                <Phone className="w-5 h-5 text-[#4A8FD1]" />
              </div>
              <div className="flex-1">
                <div className="text-xs mono uppercase tracking-widest text-slate-400 mb-1">
                  {t('contact.phone')}
                </div>
                <div className="font-medium text-slate-50 mono text-sm">
                  {profile.phone}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[#4A8FD1] mt-2" />
            </a>

            <a
              href={profile.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin-link"
              className="flex items-start gap-4 p-5 bg-[#1A2231] border border-white/10 rounded-lg hover:border-[#4A8FD1] hover:shadow-sm transition-all group"
            >
              <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#4A8FD1]/10">
                <Linkedin className="w-5 h-5 text-[#4A8FD1]" />
              </div>
              <div className="flex-1">
                <div className="text-xs mono uppercase tracking-widest text-slate-500 mb-1">
                  {t('contact.linkedin')}
                </div>
                <div className="font-medium text-slate-50">linkedin.com/in/sagarvm</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#4A8FD1] mt-2" />
            </a>

            <div className="flex items-start gap-4 p-5 bg-[#1A2231] border border-white/10 rounded-lg">
              <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#4A8FD1]" />
              </div>
              <div className="flex-1">
                <div className="text-xs mono uppercase tracking-widest text-slate-500 mb-1">
                  {language === 'en' ? 'Location' : 'Standort'}
                </div>
                <div className="font-medium text-slate-50">
                  {profile.location[language]}
                </div>
              </div>
            </div>

            <a
              href={profile.cv}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleCVDownload}
              data-testid="contact-cv-link"
              className="w-full flex items-center justify-between gap-4 p-5 bg-[#4A8FD1] text-slate-50 rounded-lg hover:bg-[#6AAFE0] transition-colors group"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Download className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <div className="text-xs mono uppercase tracking-widest text-white/80 mb-1">
                    PDF
                  </div>
                  <div className="font-medium">{t('contact.downloadCV')}</div>
                </div>
              </div>
              <ArrowRight className="w-4 h-4" />
            </a>
            <p className="text-xs text-slate-500 mono px-1">{t('contact.cvNote')}</p>
          </aside>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              className="p-6 sm:p-8 bg-[#1A2231] border border-white/10 rounded-lg space-y-5"
              data-testid="contact-form"
            >
              {/* Honeypot — real users leave it empty; bots fill it. */}
              <input
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={form.website || ''}
                onChange={handleChange}
                aria-hidden="true"
                className="absolute w-px h-px overflow-hidden opacity-0 pointer-events-none"
                style={{ left: '-9999px' }}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-xs mono uppercase tracking-widest text-slate-400">
                    {t('contact.name')}
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder={t('contact.placeholderName')}
                    required
                    className="h-11"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs mono uppercase tracking-widest text-slate-400">
                    {t('contact.email')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder={t('contact.placeholderEmail')}
                    required
                    className="h-11"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="subject" className="text-xs mono uppercase tracking-widest text-slate-400">
                  {t('contact.subject')}
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  placeholder={t('contact.placeholderSubject')}
                  required
                  className="h-11"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="message" className="text-xs mono uppercase tracking-widest text-slate-400">
                  {t('contact.message')}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder={t('contact.placeholderMessage')}
                  required
                  rows={6}
                  className="resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                size="lg"
                className="w-full sm:w-auto bg-[#4A8FD1] hover:bg-[#6AAFE0] h-12 px-6"
              >
                {loading ? (
                  t('contact.sending')
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('contact.send')}
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>

        {/* QR Code section */}
        <section className="mt-16 pt-10 border-t border-white/10">
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-8 items-center">
            <div className="sm:col-span-4 flex justify-center sm:justify-start">
              <div className="p-4 bg-[#1A2231] border border-white/10 rounded-lg shadow-sm">
                {siteUrl ? (
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=4&color=1F3A5F&bgcolor=FFFFFF&data=${encodeURIComponent(siteUrl)}`}
                    alt={t('qr.title')}
                    width="220"
                    height="220"
                    className="block"
                  />
                ) : (
                  <div className="w-[220px] h-[220px] bg-white/10 animate-pulse" />
                )}
              </div>
            </div>
            <div className="sm:col-span-8">
              <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#4A8FD1] mb-3">
                <QrCode className="w-4 h-4" />
                <span>QR</span>
              </div>
              <h3 className="text-2xl font-bold text-slate-50 mb-2">
                {t('qr.title')}
              </h3>
              <p className="text-slate-400 leading-relaxed max-w-md">
                {t('qr.hint')}
              </p>
              <p className="mt-4 text-xs mono text-slate-400 break-all">
                {siteUrl}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Contact;
